// packages/console/src/components/panels/LiveIngestPanel.tsx
//
// Ingest panel for the LIVE console, ported from the Bonded-source block of
// docs/design/ui_kits/aether-live/Transcoder.jsx. It reads REAL data off the
// live SSE stream (LiveStream + LiveLink), not a sim tick: the bonded input
// rate and link RTT as readouts, one row per bonded WAN link (per-link bitrate,
// a Meter, and its carrier), then a source spec block.
//
// Honest absence (R10): when the session is stopped or a figure is absent, the
// stream sends null and every readout falls to an em-dash. The link Meter reads
// 0 for a down/idle link; a down link's Dot idles.

import { useState, type CSSProperties } from "react";
import { Panel, Read, Meter, Dot, Row, Eb } from "../Parts";
import { Icon } from "../Icons";
import type { LiveLink, LiveStream } from "../../api/live";
import { EM_DASH, fmtMbps, fmtNum, fmtPct, mbps } from "../../lib/format";

// LINK_METER_MAX is the per-link Meter's full-scale, in Mb/s. A bonded WAN link
// tops out around 10 Mb/s in the design; the Meter shows the link's share of
// that ceiling. It is a display scale only, never a data source.
const LINK_METER_MAX = 10;

// CopyField renders a read-only mono value with a copy affordance so the
// broadcaster lifts the SRT ingest URL and stream key straight into their
// encoder. It shows a brief "copied" confirmation and falls back silently when
// the clipboard API is unavailable (embedded webviews), never faking success.
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          title={value}
          style={{
            flex: 1,
            font: "400 11px var(--font-mono)",
            color: "var(--fg2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </span>
        <button
          onClick={copy}
          title={`Copy ${label.toLowerCase()}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 7px",
            borderRadius: "var(--r-sm)",
            border: "1px solid var(--line-strong)",
            background: "transparent",
            color: copied ? "var(--blue-400)" : "var(--fg3)",
            font: "var(--t-micro)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {copied && <Icon name="check" size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

// KEY_PATTERN mirrors the server's validation (letters, digits, dot, dash,
// underscore, 3..128 chars) so an invalid key is caught inline before the POST.
const KEY_PATTERN = /^[A-Za-z0-9._-]{3,128}$/;

// EditableKeyField shows the stream key with an Edit affordance. Editing persists
// the key (onSave), so a broadcaster can present a memorable key; the SRT URL
// above rebuilds from the returned record. A local charset check gates the save,
// and a server rejection (e.g. a key already in use) surfaces inline.
function EditableKeyField({
  value,
  onSave,
  busy,
}: {
  value: string;
  onSave: (key: string) => Promise<unknown>;
  busy?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };
  const begin = () => {
    setDraft(value);
    setErr(null);
    setEditing(true);
  };
  const save = async () => {
    const key = draft.trim();
    if (!KEY_PATTERN.test(key)) {
      setErr("3 to 128 chars: letters, digits, dot, dash, underscore.");
      return;
    }
    try {
      await onSave(key);
      setEditing(false);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "could not update the key");
    }
  };

  const btn: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 7px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--line-strong)",
    background: "transparent",
    color: "var(--fg3)",
    font: "var(--t-micro)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        Stream key
      </span>
      {editing ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              value={draft}
              autoFocus
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
                if (e.key === "Escape") setEditing(false);
              }}
              style={{
                flex: 1,
                font: "400 11px var(--font-mono)",
                color: "var(--fg1)",
                background: "var(--bg-input)",
                border: `1px solid ${err ? "var(--err)" : "var(--line-strong)"}`,
                borderRadius: "var(--r-sm)",
                padding: "4px 7px",
                minWidth: 0,
              }}
            />
            <button onClick={() => void save()} disabled={busy} title="Save the stream key" style={{ ...btn, color: "var(--blue-400)", borderColor: "var(--blue-500)" }}>
              Save
            </button>
            <button onClick={() => setEditing(false)} title="Cancel" style={btn}>
              Cancel
            </button>
          </div>
          {err && <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>{err}</span>}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            title={value}
            style={{
              flex: 1,
              font: "400 11px var(--font-mono)",
              color: "var(--fg2)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </span>
          <button onClick={copy} title="Copy stream key" style={{ ...btn, color: copied ? "var(--blue-400)" : "var(--fg3)" }}>
            {copied && <Icon name="check" size={11} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button onClick={begin} title="Set a custom stream key" style={btn}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

export function LiveIngestPanel({
  stream,
  links,
  onAir,
  ingestUrl,
  streamKey,
  onSetKey,
  busy,
}: {
  stream: LiveStream | null;
  links: LiveLink[];
  onAir: boolean;
  // The provisioned stream's ingest endpoint. Absent until a stream is created.
  ingestUrl?: string | null;
  streamKey?: string | null;
  // onSetKey persists a custom stream key for the current stream. Absent (the key
  // stays read-only) until a stream is provisioned.
  onSetKey?: (key: string) => Promise<unknown>;
  busy?: boolean;
}) {
  return (
    <Panel
      title="Ingest"
      right={<span style={{ font: "400 10px var(--font-mono)", color: "var(--fg3)" }}>SRT :9000</span>}
    >
      <Eb style={{ marginBottom: 9 }}>Ingest endpoint</Eb>
      {ingestUrl && streamKey ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          <CopyField label="SRT URL" value={ingestUrl} />
          {onSetKey ? (
            <EditableKeyField value={streamKey} onSave={onSetKey} busy={busy} />
          ) : (
            <CopyField label="Stream key" value={streamKey} />
          )}
        </div>
      ) : (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)", marginBottom: 14 }}>
          Create a stream to get an ingest URL
        </div>
      )}
      <div style={{ height: 1, background: "var(--line)", margin: "0 0 12px" }} />
      <Eb style={{ marginBottom: 9 }}>Bonded source</Eb>
      <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
        <Read value={fmtMbps(stream?.ingestBitsPerSec ?? null)} unit="Mb/s" label="Input rate" />
        <Read value={fmtNum(stream?.ingestRttMs ?? null, 0)} unit="ms" label="Link rtt" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {links.length === 0 ? (
          <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
            {onAir ? "No bonded links reported" : EM_DASH}
          </div>
        ) : (
          links.map((l) => {
            const rate = mbps(l.bitsPerSec);
            return (
              <div key={l.linkId} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
                  <Dot tone={l.up ? "ok" : "idle"} />
                  <span
                    style={{
                      font: "500 10px var(--font-sans)",
                      letterSpacing: ".1em",
                      textTransform: "uppercase",
                      color: "#fff",
                    }}
                  >
                    {l.name}
                  </span>
                  <span
                    style={{
                      font: "var(--t-micro)",
                      color: "var(--fg4)",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.carrier ?? EM_DASH}
                  </span>
                  <span
                    style={{
                      font: "400 11px var(--font-mono)",
                      color: "var(--fg2)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtMbps(l.bitsPerSec, 1)}
                  </span>
                </div>
                <Meter
                  pct={rate !== null ? (rate / LINK_METER_MAX) * 100 : 0}
                  color={l.up ? "var(--viz-2)" : "var(--idle)"}
                />
              </div>
            );
          })
        )}
      </div>
      <div style={{ height: 1, background: "var(--line)", margin: "14px 0 12px" }} />
      <Row label="Source" value={sourceSpec(stream)} />
      <Row label="Link loss" value={lossSpec(links)} />
    </Panel>
  );
}

// sourceSpec renders the source name (the stream's own name) or an em-dash when
// there is no session. The design's fixed "3840×2160p60 / HEVC 10-bit / NVDEC"
// spec was sim-authored; the real source identity is the stream name the
// backend reports, so we show that and fall back to em-dash absent a session.
function sourceSpec(stream: LiveStream | null): string {
  return stream && stream.name ? stream.name : EM_DASH;
}

// lossSpec reports the worst per-link packet loss across the bonded set, shown
// as a percent: the highest single-link loss is the one the operator watches
// (the bond masks it, but a link past its loss budget is the failure mode).
// Absent any link loss sample it is an em-dash (honest absence).
function lossSpec(links: LiveLink[]): string {
  const samples = links.map((l) => l.lossPct).filter((v): v is number => v !== null);
  if (samples.length === 0) return EM_DASH;
  return fmtPct(Math.max(...samples));
}
